import { useState, useMemo } from 'react';
import type { Word } from '../types/word.types';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import { Check, X, RotateCcw } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import WordListItem from './LearnWords/WordListItem';

interface BlankItem {
  id: string;
  correctWord: string;
  userAnswer: string | null;
}

interface FillInTheBlankDemoProps {
  paragraph: string;
  answers: string[];
  words: Word[];
  title?: string;
  description?: string;
}

export const FillInTheBlankDemo = ({
  paragraph,
  answers,
  words,
  title = "Fill in the Blank",
  description = "Complete the paragraph by selecting the correct words from the dropdowns"
}: FillInTheBlankDemoProps) => {
  const [blanks, setBlanks] = useState<BlankItem[]>(() =>
    answers.map((answer, index) => ({
      id: `blank-${index}`,
      correctWord: answer,
      userAnswer: null,
    }))
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionResults, setSubmissionResults] = useState<boolean[]>([]);
  const [selectedWordForDetails, setSelectedWordForDetails] = useState<Word | null>(null);

  // Parse paragraph from text
  const paragraphParts = useMemo(() => {
    const parts: Array<{ type: 'text' | 'blank'; content: string; blankId?: string }> = [];
    const blankPattern = /__(\d+)__/g;
    let lastIndex = 0;
    let match;

    while ((match = blankPattern.exec(paragraph)) !== null) {
      // Add text before blank
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: paragraph.substring(lastIndex, match.index),
        });
      }

      // Add blank
      const blankNum = parseInt(match[1]) - 1;
      if (blankNum < answers.length) {
        const blankId = `blank-${blankNum}`;
        parts.push({
          type: 'blank',
          content: '',
          blankId,
        });
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < paragraph.length) {
      parts.push({
        type: 'text',
        content: paragraph.substring(lastIndex),
      });
    }

    return parts;
  }, [paragraph, answers.length]);

  const handleAnswerChange = (blankId: string, answer: string) => {
    setBlanks(prev =>
      prev.map(blank =>
        blank.id === blankId ? { ...blank, userAnswer: answer } : blank
      )
    );
  };

  const handleSubmit = () => {
    const results = blanks.map(blank =>
      blank.userAnswer?.toLowerCase() === blank.correctWord.toLowerCase()
    );
    setSubmissionResults(results);
    setIsSubmitted(true);
  };

  const handleReset = () => {
    setBlanks(prev =>
      prev.map((blank, index) => ({
        ...blank,
        userAnswer: submissionResults[index] ? blank.correctWord : null
      }))
    );
    setIsSubmitted(false);
  };

  const allAnswered = blanks.every(blank => blank.userAnswer !== null);
  const correctCount = blanks.filter(
    blank => blank.userAnswer?.toLowerCase() === blank.correctWord.toLowerCase()
  ).length;

  // Words for dropdown options - sorted alphabetically
  const dropdownWords = useMemo(() => {
    return words
      .map(word => word.word)
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [words]);

  // Track which words are used
  const usedWords = useMemo(() => {
    return new Set(blanks.map(blank => blank.userAnswer).filter(Boolean));
  }, [blanks]);

  return (
    <div className="w-full space-y-4 md:space-y-6">
      {/* Header */}
      <div className="mb-2 md:mb-3">
        <div className="flex flex-col space-y-4 md:flex-row md:items-start md:justify-between md:space-y-0">
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">{title}</h1>
            <p className="text-muted-foreground text-sm md:text-base">
              {description}
            </p>
          </div>
        </div>
      </div>

      {/* Progress */}
      {isSubmitted && (
        <Card className="mb-4 md:mb-6">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Your Score</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold">{correctCount}</p>
                  <p className="text-lg text-muted-foreground">/ {blanks.length}</p>
                </div>
              </div>
              <div className={cn(
                "text-4xl font-bold",
                correctCount === blanks.length ? "text-green-600" : "text-orange-600"
              )}>
                {Math.round((correctCount / blanks.length) * 100)}%
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Word Bank Reference */}
      <Card className="mt-3">
        <CardContent className="p-3 md:p-4">
          <div className="mb-3">
            <h3 className="text-sm md:text-base font-semibold">Word Bank</h3>
            <p className="text-sm text-muted-foreground">
              Click any word for details
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {words.sort((a, b) => a.word.toLowerCase().localeCompare(b.word.toLowerCase())).map((word) => (
              <button
                key={word.id}
                onClick={() => setSelectedWordForDetails(word)}
                className="px-3 py-1.5 text-sm bg-muted hover:bg-muted/80 rounded-full border transition-colors"
              >
                {word.word}
              </button>
            ))}
          </div>

          {/* Word Details Modal */}
          <Dialog open={!!selectedWordForDetails} onOpenChange={() => setSelectedWordForDetails(null)}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden p-0">
              <div className="p-6 pb-4" />
              <div className="px-6 pb-6 max-h-[60vh] overflow-y-auto">
                {selectedWordForDetails && (
                  <WordListItem
                    word={selectedWordForDetails}
                    onDelete={() => {}}
                    onRatingChange={() => {}}
                    preview={true}
                  />
                )}
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* Paragraph with Blanks */}
      <Card className="mt-3">
        <CardContent className="p-4 md:p-8">
          <div className="text-base md:text-lg space-y-3 md:space-y-4">
            <p className="leading-[2.5rem] md:leading-[3rem]">
              {paragraphParts.map((part, index) => {
                if (part.type === 'text') {
                  // Regular text - no hover effects
                  return (
                    <span key={index}>
                      {part.content}
                    </span>
                  );
                } else {
                  const blank = blanks.find(b => b.id === part.blankId);
                  if (!blank) return null;

                  const isCorrect = blank.userAnswer?.toLowerCase() === blank.correctWord.toLowerCase();
                  const showResult = isSubmitted && blank.userAnswer;

                  return (
                    <span key={index} className="inline-flex items-center mx-0.5 my-1">
                      <Select
                        value={blank.userAnswer || ''}
                        onValueChange={(value) => handleAnswerChange(blank.id, value)}
                        disabled={isSubmitted}
                      >
                        <SelectTrigger
                          className={cn(
                            "w-[140px] sm:w-[160px] md:w-[200px] inline-flex transition-all",
                            blank.userAnswer === null && !isSubmitted && "border-dashed border-2 border-muted-foreground/50",
                            blank.userAnswer !== null && !isSubmitted && "border-primary/50 bg-primary/5",
                            showResult && (isCorrect ? "border-green-500 bg-green-50" : "border-red-500 bg-red-50")
                          )}
                        >
                          <SelectValue placeholder="Select word...">
                            {blank.userAnswer || "Select word..."}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {dropdownWords.map((word) => {
                            const isUsed = usedWords.has(word);
                            return (
                              <SelectItem
                                key={word}
                                value={word}
                                disabled={isUsed}
                                className="flex items-center justify-between"
                              >
                                <span className={cn(isUsed && "text-muted-foreground")}>{word}</span>
                                {isUsed && (
                                  <span className="text-xs font-medium text-green-600 ml-auto pl-2">used</span>
                                )}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {showResult && (
                        <span className="ml-1">
                          {isCorrect ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <X className="h-4 w-4 text-red-600" />
                          )}
                        </span>
                      )}
                    </span>
                  );
                }
              })}
            </p>

            {/* Quiz Controls */}
            {!isSubmitted && (
              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center justify-end mb-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReset}
                      className="gap-1"
                    >
                      <RotateCcw className="h-4 w-4" />
                      <span>Clear</span>
                    </Button>
                  </div>
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={!allAnswered}
                  className="w-full"
                  size="lg"
                >
                  Check Answers
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Try Again Button */}
      {isSubmitted && correctCount < blanks.length && (
        <div className="mt-8 flex justify-center">
          <Button
            size="lg"
            onClick={handleReset}
          >
            Try Again
          </Button>
        </div>
      )}

      {/* Success Message */}
      {isSubmitted && correctCount === blanks.length && (
        <Card className="mt-2 border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/20">
          <CardContent className="p-3 text-center">
            <div className="mb-2">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-green-100 rounded-full mb-2 dark:bg-green-900">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-green-800 dark:text-green-400">
                Excellent! 🎉
              </h3>
            </div>

            <div className="flex justify-center pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="text-xs"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FillInTheBlankDemo;
